// Derived state — `statusOf`, `pct`, `openForAI`, `nextRequired` (design D1).
// Kept as pure functions over (form, state) so they are unit-testable without
// mounting the provider and reusable by the later slices' surfaces.

import { log } from '../../Utilities/logging';
import { CfRegistry } from './formsConfig';
import { CfState } from './formsReducer';
import { CfField, CfForm, FieldStatus, FieldValue } from './types';
import { asInstances, isEmptyValue } from './valueFormat';

/** Mirrors the extraction service's `_resolve_active_fields` comparison:
 *  `value.strip().lower()`. */
const foldGate = (value: string): string => value.trim().toLowerCase();

/**
 * Does a collected value satisfy a conditional gate? Mirrors the backend's
 * `_resolve_active_fields` so both sides of the conversation hide and reveal
 * the same fields at the same time (platform contract, tracker 6.26b):
 * trimmed, case-insensitive comparison; an uncollected or empty parent value
 * never matches; a link with no usable gate value never matches (the backend's
 * `condition or ""` can equal no collected value). The gate may be an array
 * (several trigger values), and a checklist parent matches when its selection
 * includes a gate value ("parent matching extended to array-includes").
 */
const gateMatches = (parentValue: FieldValue | undefined, gate: string | string[] | undefined): boolean => {
    const gates = (gate === undefined ? [] : Array.isArray(gate) ? gate : [gate])
        .map((g) => foldGate(String(g)))
        .filter((g) => g !== '');
    if (!gates.length) return false;
    if (parentValue === undefined) return false;
    const held = (Array.isArray(parentValue) ? parentValue : [parentValue])
        .map((v) => foldGate(String(v)))
        .filter((v) => v !== '');
    return gates.some((g) => held.includes(g));
};

/** Cycles are config bugs worth one log line each, not one per render. */
const loggedCycles = new Set<string>();

/**
 * Visible only while the WHOLE parent chain matches. Each gate resolves
 * against the session-wide `values` map — one shared map across every registry
 * form — so a parent living in a different form gates exactly like an in-form
 * one, and the chain walk continues through `byId` (pass a registry-wide index
 * so a cross-form parent's own conditions count too). A parent that is
 * unknown, uncollected, or non-matching means hidden; a malformed cycle hides
 * its fields and logs once.
 */
export const isFieldVisible = (
    field: CfField,
    values: Record<string, FieldValue>,
    byId: Map<string, CfField>
): boolean => {
    let current: CfField | undefined = field;
    const seen = new Set<string>();
    while (current?.parentId) {
        if (seen.has(current.id)) {
            if (!loggedCycles.has(field.id)) {
                loggedCycles.add(field.id);
                log(
                    'edulla-chat',
                    `Conditional parent chain for field ${field.id} cycles at ${current.id}; hiding the field`,
                    'visitor',
                    { sessionId: '' }
                );
            }
            return false;
        }
        seen.add(current.id);
        if (!gateMatches(values[current.parentId], current.parentValue)) return false;
        current = byId.get(current.parentId);
    }
    return true;
};

export const indexFields = (form: CfForm): Map<string, CfField> =>
    new Map(form.fields.map((f) => [f.id, f]));

/** Fields currently on screen — hidden conditional children are excluded from
 *  rendering, progress, next-required, extraction, and validation alike.
 *  Pass the registry so parent chains resolve across every form it holds
 *  (platform contract, tracker 6.26b); without one, the walk can only follow
 *  parents defined in `form` itself — gates still check the shared values map. */
export const visibleFields = (
    form: CfForm | null,
    state: CfState,
    registry?: CfRegistry | null
): CfField[] => {
    if (!form) return [];
    const byId = registry?.fieldsById ?? indexFields(form);
    return form.fields.filter((f) => isFieldVisible(f, state.values, byId));
};

/**
 * Structural completeness for the multi-part types (design D1): a `consent`
 * with unchecked attestations or a `group` with an unfinished instance (or
 * fewer instances than `repeat.min`) must never read confirmed — it counts as
 * invalid for progress purposes even before any error message lands. Scalar
 * types are always structurally complete; emptiness is not this check's job.
 */
export const isStructurallyComplete = (field: CfField, value: FieldValue | undefined): boolean => {
    if (field.type === 'consent') {
        const held: Array<string | unknown> = Array.isArray(value) ? value : [];
        const checked = new Set(held.filter((v): v is string => typeof v === 'string'));
        return (field.options ?? []).every((option) => checked.has(option.value));
    }
    if (field.type === 'group') {
        const instances = asInstances(value);
        if (field.repeat?.min !== undefined && instances.length < field.repeat.min) return false;
        const required = (field.template ?? []).filter((sub) => !sub.optional);
        return instances.every((instance) => required.every((sub) => !isEmptyValue(instance[sub.id])));
    }
    return true;
};

/**
 * The four-state machine. Pass the field definition when you have one — the
 * multi-part types (`group`, `consent`) need it to withhold `confirmed` while
 * structurally incomplete; without it the check is value-only, as before.
 */
export const statusOf = (state: CfState, fieldId: string, field?: CfField): FieldStatus => {
    const value = state.values[fieldId];
    if (!isEmptyValue(value)) {
        if (state.fieldErrors[fieldId]) return 'invalid';
        if (field && !isStructurallyComplete(field, value)) return 'invalid';
        return 'confirmed';
    }
    return state.suggested[fieldId] !== undefined ? 'suggested' : 'empty';
};

/** `pct = round(confirmedRequired / totalRequired * 100)`. Optional fields never
 *  block completion. A form whose visible fields include no required ones — every
 *  published BotForms field ships `required: false` — reads filled/total over all
 *  visible fields instead (store tracker 6.25): "no required fields" used to
 *  collapse to a constant 100%, so an untouched bot form claimed completeness on
 *  every percent surface. No form, or a form with no visible fields, is 0. */
export const progressPct = (form: CfForm | null, state: CfState, registry?: CfRegistry | null): number => {
    if (!form) return 0;
    const visible = visibleFields(form, state, registry);
    const required = visible.filter((f) => !f.optional);
    const counted = required.length ? required : visible;
    if (!counted.length) return 0;
    const done = counted.filter((f) => statusOf(state, f.id, f) === 'confirmed').length;
    return Math.round((done / counted.length) * 100);
};

/**
 * Ready to review & submit (engagement closure, store tracker 6.55): every
 * visible required field confirmed, or — for an all-optional form — at least
 * one field filled (store tracker 6.25: an untouched all-optional form must
 * never read complete). Mirrors `FormOverlay`'s own local `complete`
 * calculation so the pinned card's persistent review-and-submit flip and the
 * overlay's own end-card copy never disagree. Derived entirely from stored
 * field values — unlike the `form_complete` wire signal (which drives the
 * one-time inline closure card), this is live and persists across reloads
 * with no signal of its own required.
 */
export const isFormComplete = (form: CfForm | null, state: CfState, registry?: CfRegistry | null): boolean => {
    if (!form) return false;
    const visible = visibleFields(form, state, registry);
    if (!visible.length) return false;
    const allOptional = visible.every((f) => f.optional);
    if (allOptional) return visible.some((f) => statusOf(state, f.id, f) === 'confirmed');
    return progressPct(form, state, registry) === 100;
};

/**
 * The open-field list sent with every turn (design D5). Excludes confirmed
 * fields so extraction can never re-fill them, and the guardrail types the
 * platform forbids from extraction entirely. `collect_via_bot` is the tenant's
 * per-field opt-in — a field without it is form-only.
 */
export const openForAI = (form: CfForm | null, state: CfState, registry?: CfRegistry | null): CfField[] =>
    visibleFields(form, state, registry).filter(
        (f) => statusOf(state, f.id, f) !== 'confirmed' && f.collectViaBot && !f.noAI
    );

/** First required field still needing an answer — drives the quick-reply chips. */
export const nextRequired = (
    form: CfForm | null,
    state: CfState,
    registry?: CfRegistry | null
): CfField | undefined =>
    visibleFields(form, state, registry).find((f) => {
        if (f.optional) return false;
        const status = statusOf(state, f.id, f);
        return status === 'empty' || status === 'invalid';
    });

/**
 * The field the quick-reply chips render for (store ledger 6.108, chips-follow-
 * ask D3). Before any response has carried `ask_field_id` (a backend predating
 * it) the local rule stands: `nextRequired`. Once signalled, only the server's
 * ask counts: it must have been signalled for the active form, belong to it,
 * be visible, still be unanswered (`empty`/`invalid` — a field since filled or
 * confirmed in the form view gets no chips even if the ask is stale), the ask
 * must not be an inline ask (`askInline` — its control renders in the chat),
 * and be a
 * `radio` or a `select` (a 6+-option ask gets one "Choose from N options"
 * picker button instead of per-option chips; its options also ride the prose,
 * 6.105). Otherwise undefined — no row.
 */
export const chipField = (
    form: CfForm | null,
    state: CfState,
    registry?: CfRegistry | null
): CfField | undefined => {
    if (!state.askSignalled) return nextRequired(form, state, registry);
    // Inline sensitive asks: the field's own control renders in the chat; a
    // chip tap would send the option label as a chat message (the value in
    // text), which is exactly what the inline control exists to avoid.
    if (state.askInline) return undefined;
    if (!form || !state.askFieldId) return undefined;
    if (state.askFormId !== state.activeFormId) return undefined;
    const field = visibleFields(form, state, registry).find((f) => f.id === state.askFieldId);
    if (!field || (field.type !== 'radio' && field.type !== 'select')) return undefined;
    const status = statusOf(state, field.id, field);
    return status === 'empty' || status === 'invalid' ? field : undefined;
};

/**
 * Pending suggestions for one form's fields. Per-form because the shared map
 * may hold a paused form's suggestions — the active card's review count must
 * never claim another form's pending answers. Null form counts everything
 * (single-form legacy behavior).
 */
export const suggestedCount = (form: CfForm | null, state: CfState): number => {
    const ids = Object.keys(state.suggested);
    if (!form) return ids.length;
    const owned = new Set(form.fields.map((f) => f.id));
    return ids.filter((id) => owned.has(id)).length;
};

/** Every field the active form owns — the set discard clears. */
export const allFieldIds = (form: CfForm | null): string[] => (form ? form.fields.map((f) => f.id) : []);

/**
 * A form's lifecycle state for the menu (design D4: one obvious action per
 * state). `saved` vs `idle` (Not started) is decided by the caller from the
 * form's derived progress — this reads only the lifecycle lists.
 */
export type CfFormLifecycle = 'active' | 'queued' | 'submitted' | 'discarded' | 'idle';

export const formLifecycle = (state: CfState, formId: string): CfFormLifecycle => {
    if (state.activeFormId === formId) return 'active';
    if (state.submittedFormIds.includes(formId)) return 'submitted';
    if (state.discardedFormIds.includes(formId)) return 'discarded';
    if (state.queuedForms.includes(formId)) return 'queued';
    return 'idle';
};

/** Does the form hold at least one saved value? The menu's "Saved · n%" vs
 *  "Not started" test, shared with the Home list's filter. */
export const isFormTouched = (form: CfForm, state: CfState): boolean =>
    form.fields.some((f) => state.values[f.id] !== undefined);

/**
 * The Home tab's rule (spec 2026-10-16): a visitor sees only forms there is
 * something to come back to — active, queued, submitted, or idle with a saved
 * value. A browsable idle form with nothing saved ("Not started") stays off
 * Home (the chat menu still lists it); discarded and ambient forms never
 * appear. The Home gate and its rows share this one predicate so the list can
 * never render an empty header.
 */
export const isResumableForm = (form: CfForm, state: CfState): boolean => {
    if (!form.browsable) return false;
    const lifecycle = formLifecycle(state, form.objectId);
    if (lifecycle === 'discarded') return false;
    return lifecycle !== 'idle' || isFormTouched(form, state);
};

/**
 * Forms-corrections slice (D2): does this form have an unsent post-submit
 * edit? Only meaningful for a submitted form, but reads safely for any id —
 * the menu row and the pinned-area warning share this one check.
 */
export const isFormDirty = (state: CfState, formId: string): boolean =>
    state.dirtyFormIds.includes(formId);

/* ---------------------------------------------------------------
   Inline sensitive asks (store change
   `conversational-forms-inline-sensitive-asks`, slice 1: radio and text).
----------------------------------------------------------------*/

/** The control types this build can render inside the chat. The widget lists
 *  only fields of these types in `inline_field_ids`, so a server never asks
 *  inline for a control this build lacks. */
const INLINE_RENDERABLE_TYPES: ReadonlyArray<CfField['type']> = ['radio', 'text'];

/** Can this field be asked through an inline control in this build? Sensitive,
 *  form-only (Ask in chat off), AI-eligible, and of a renderable type. */
export const isInlineEligible = (field: CfField): boolean =>
    field.sensitive === true &&
    !field.collectViaBot &&
    !field.noAI &&
    INLINE_RENDERABLE_TYPES.includes(field.type);

/** The ids sent as `inline_field_ids`: the ACTIVE form's visible inline-
 *  eligible fields. Ids only — never anything about a value. */
export const inlineFieldIds = (
    form: CfForm | null,
    state: CfState,
    registry?: CfRegistry | null
): string[] =>
    form && state.activeFormId === form.objectId
        ? visibleFields(form, state, registry).filter(isInlineEligible).map((f) => f.id)
        : [];

/**
 * The field whose inline control is LIVE right now, or undefined. Live means:
 * the server's latest ask is an inline ask for this field, signalled for the
 * active form, the form is active and not submitted/discarded, the field is
 * visible, renderable, and still has no value. `messageFieldIds` are the
 * `inlineAskFieldId`s present in the chat history; the ask must have a message
 * to live in (a stale persisted signal with no message shows nothing).
 */
export const liveInlineField = (
    state: CfState,
    registry: CfRegistry | null | undefined,
    messageFieldIds: ReadonlyArray<string | undefined>
): CfField | undefined => {
    if (!registry || !state.askSignalled || !state.askInline || !state.askFieldId) return undefined;
    const formId = state.activeFormId;
    if (!formId || state.askFormId !== formId) return undefined;
    if (state.submittedFormIds.includes(formId) || state.discardedFormIds.includes(formId)) return undefined;
    const form = registry.byId.get(formId);
    if (!form) return undefined;
    const field = visibleFields(form, state, registry).find((f) => f.id === state.askFieldId);
    if (!field || !isInlineEligible(field)) return undefined;
    if (!isEmptyValue(state.values[field.id])) return undefined;
    return messageFieldIds.includes(field.id) ? field : undefined;
};
